// "Before BloodFlow": a big cursor travels across the EMR's OWN real tabs,
// opening cascading windows of cramped, cryptic data — then the clinician
// types a short, messy, unstructured note. No fake HUD/stopwatch.
import React, { useState, useEffect, useRef } from 'react';
import { MESSY, HUNT_SOURCES } from './emrScenarios';

const NAV_SELECTORS = {
  epic:     '.bf-ep2-tab',
  cerner:   '.bf-cn2-mi',
  glintt:   '.gl-card-h',
  trakcare: '.bf-tc2-act',
  orbis:    '.bf-ob2-ni',
  emis:     '.bf-em2-ti',
  systmone: '.bf-s12-ti',
  sclinico: '.bf-sc2-row:not(.empty)',
};
const NAV_STEP_MS = 1150;
const NAV_MAX = 5;

// cryptic, hard-to-read data windows that "open" as the cursor clicks around
const MESSY_TABLES = [
  { title: 'Laboratory results', head: ['Test', 'Val', 'Fl', 'Ref'], rows: [
    ['HGB', '10.4', 'L↓', '12-16'], ['HCT', '30.8', 'L↓', '36-46'], ['WBC', '11.2', 'H↑', '4-11'],
    ['PLT', '302', '', '150-400'], ['NEUT', '4.93', '', '2-7.5'], ['CRE', '1.6', 'H↑', '0.6-1.1'],
    ['CA15-3', '49', '?', '<30'], ['ALT', '62', 'H↑', '<40'], ['eGFR', '—', '?', '>60'],
  ]},
  { title: 'Medications', head: ['Drug', 'Dose', 'Fq', 'Last'], rows: [
    ['PACLITAXEL', '80mg/m²', 'wk', '05/12'], ['PERTUZUMAB', '420mg', 'q3w', '—'], ['TRASTUZ.', '600mg', 'q3w', '—'],
    ['ONDANSET.', '8mg', 'PRN', '?'], ['DEXAMETH', '8mg', 'pre', '?'], ['ENOXAP.', '40mg', 'OD', '07:30'],
  ]},
  { title: 'Imaging · PACS', head: ['Study', 'Date', 'Status'], rows: [
    ['CT TAP', '29/08', 'reported'], ['US BREAST', '—', 'not done'], ['ECHO', '30/10', 'LVEF 56'],
    ['MMG', '—', '?'], ['PET-CT', '—', 'req?'], ['MRI', '—', '—'],
  ]},
  { title: 'Previous notes', head: ['Date', 'By', 'Type'], rows: [
    ['11/02', '—', 'prog?'], ['05/12', 'SM', 'tx start'], ['13/11', '—', 'onc r/v'],
    ['10/09', 'JC', 'consult'], ['29/08', '—', 'MDT'], ['—', '—', 'scan rpt'],
  ]},
  { title: 'Allergies / alerts', head: ['Agent', 'Type', 'Sev'], rows: [
    ['PENICILLIN', 'drug', '—'], ['?LATEX', '?', '?'], ['NKDA?', '', 'chk'], ['—', '—', '—'],
  ]},
];

const CURSOR_SVG = (
  <svg className="bf-cursor" width="34" height="34" viewBox="0 0 24 24">
    <path d="M5 2.5l13.5 7-5.7 1.4 3.2 6.6-2.7 1.3-3.2-6.7-4.4 4z" fill="#fff" stroke="#161616" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

function MessyWin({ p }) {
  const ds = p.data;
  const cols = `1.5fr ${Array(ds.head.length - 1).fill('1fr').join(' ')}`;
  return (
    <div className="bf-win" style={{ left: p.x, top: p.y }}>
      <div className="bf-win-t">{p.title}<span className="x">✕</span></div>
      <div className="bf-win-b">
        <span className="bf-copied">⧉ copied</span>
        <div className="bf-mrow h" style={{ gridTemplateColumns: cols }}>
          {ds.head.map((h, i) => <span key={i}>{h}</span>)}
        </div>
        {ds.rows.map((r, ri) => (
          <div className="bf-mrow" key={ri} style={{ gridTemplateColumns: cols }}>
            {r.map((c, ci) => <span key={ci} className={/[↑↓?]/.test(c) ? 'bf-flag' : ''}>{c}</span>)}
          </div>
        ))}
      </div>
    </div>
  );
}

export function BeforeNav({ emrId, token }) {
  const wrapRef = useRef(null);
  const [cursor, setCursor] = useState({ x: 640, y: 440 });
  const [panels, setPanels] = useState([]);
  const [clickKey, setClickKey] = useState(0);

  useEffect(() => {
    const wrap = wrapRef.current; if (!wrap) return;
    const canvas = wrap.closest('.emr-canvas'); if (!canvas) return;
    const sel = NAV_SELECTORS[emrId];
    const els = sel ? Array.from(canvas.querySelectorAll(sel)).slice(0, NAV_MAX) : [];
    if (!els.length) return;
    const crect = canvas.getBoundingClientRect();
    const scale = crect.width / 1280;
    const pos = el => { const r = el.getBoundingClientRect(); return { x: (r.left - crect.left) / scale, y: (r.top - crect.top) / scale, w: r.width / scale, h: r.height / scale }; };
    let i = 0, prevEl = null;
    const step = () => {
      if (prevEl) { prevEl.classList.remove('bf-nav-cur'); prevEl.classList.add('bf-nav-seen'); }
      const el = els[i]; const p = pos(el);
      el.classList.add('bf-nav-cur');
      setCursor({ x: p.x + Math.min(p.w * 0.5, 60), y: p.y + p.h * 0.6 });
      setClickKey(k => k + 1);
      const ds = MESSY_TABLES[i % MESSY_TABLES.length];
      const id = i, px = 250 + i * 40, py = 150 + i * 34;
      setPanels(prev => [...prev.slice(-2), { id, title: (HUNT_SOURCES[i] || ds.title), data: ds, x: px, y: py }]);
      prevEl = el; i += 1;
    };
    const startId = setTimeout(step, 350);
    const iv = setInterval(() => {
      if (i >= els.length) {
        clearInterval(iv);
        if (prevEl) { prevEl.classList.remove('bf-nav-cur'); prevEl.classList.add('bf-nav-seen'); }
        setTimeout(() => setPanels([]), 1300); // clear the window pile before the note finishes
        return;
      }
      step();
    }, NAV_STEP_MS);
    return () => { clearTimeout(startId); clearInterval(iv); els.forEach(e => e.classList.remove('bf-nav-cur', 'bf-nav-seen')); };
  }, [emrId, token]);

  return (
    <div className="bf-nav-layer" ref={wrapRef}>
      {panels.map(p => <MessyWin key={p.id} p={p} />)}
      <span className="bf-click" key={clickKey} style={{ left: cursor.x, top: cursor.y }}></span>
      <div style={{ position: 'absolute', left: cursor.x, top: cursor.y }}>{CURSOR_SVG}</div>
    </div>
  );
}

const ICON_ARROW = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

export function MessyNote({ emrId, onWrite }) {
  const full = (MESSY && MESSY[emrId]) || '';
  // first chunk gets pasted in (raw copy from the chart); the rest is hand-typed
  let cut = Math.floor(full.length * 0.5);
  const dot = full.indexOf('. ', cut);
  if (dot > -1) cut = dot + 2;
  const pasteText = full.slice(0, cut);
  const typeText = full.slice(cut);
  const [n, setN] = useState(0);
  const [phase, setPhase] = useState('hunting'); // hunting -> pasting -> typing -> done
  const nWin = Math.min(MESSY_TABLES.length, NAV_MAX);

  useEffect(() => {
    setN(0); setPhase('hunting');
    const timers = []; let i = 0, typeId = null;
    const pasteAt = 350 + nWin * NAV_STEP_MS + 300;
    timers.push(setTimeout(() => setPhase('pasting'), pasteAt));
    timers.push(setTimeout(() => {
      setPhase('typing');
      typeId = setInterval(() => {
        i += 2;
        if (i >= typeText.length) { i = typeText.length; setN(i); clearInterval(typeId); setPhase('done'); }
        else setN(i);
      }, 28);
    }, pasteAt + 750));
    return () => { timers.forEach(clearTimeout); if (typeId) clearInterval(typeId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emrId]);

  return (
    <div className="messy">
      <div className="messy-h">
        <span className="lbl">Free-text note</span>
        <span>· no template</span>
        <span className="warn">{phase === 'done' ? 'unstructured' : phase === 'hunting' ? 'gathering data…' : 'copy-pasting…'}</span>
      </div>
      <div className="messy-txt">
        {phase === 'hunting'
          ? <span className="messy-hint">Opening labs, meds, imaging, old notes — copying values across by hand…<span className="messy-caret"></span></span>
          : (
            <span>
              <span className="messy-pasted"><span className="ptag">⧉ pasted from chart</span>{pasteText}</span>
              {typeText.slice(0, n)}
              {phase === 'typing' ? <span className="messy-caret"></span> : null}
            </span>
          )}
      </div>
      {phase === 'done' && (
        <div className="messy-foot">
          <span className="mf-note">⚠ part pasted from the chart, rest typed by hand · no structure · easy to miss something</span>
          <button className="messy-cta" onClick={onWrite}>{ICON_ARROW} See it with BloodFlow</button>
        </div>
      )}
    </div>
  );
}
